import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import Table from "../../../../../components/table/table";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { ClearAllOutlined } from "@mui/icons-material";
import TableHeaderTextField from "../../../../../components/table-header-text-filed/table-header-text-filed";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import { useDispatch, useSelector } from "react-redux";
import { getClientDocumentsRequest } from "../../../../../redux/clients/slice";
import Loading from "../../../../../components/loading/loading";
import ErrorNotification from "../../../../../components/error-notification/error-notification";
import AddDocument from "./add-document/add-document";
import dayjs from "dayjs";
import TableHeaderDatePicker from "../../../../../components/table-header-datepicker/table-header-datepicker";
import AddIcon from "@mui/icons-material/Add";
import {
  deleteClientDocumentRequest,
  downloadTemporaryFileRequest,
} from "../../../../../utils/APICalls";

type props = {
  data?: any;
};

export default function ClientDocuments({ data }: props) {
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [filterParams, setFilterParams] = useState({});
  const [paginationModel, setPaginationModel] = useState<any>();
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [rowData, setRowData] = useState<any[]>([]);
  const [showLoader, setShowLoader] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [filterResetKey, setFilterResetKey] = useState(0);

  const dispatch = useDispatch();

  const { clientDocuments } = useSelector((state: any) => state?.clients);

  const rowCountRef = useRef(clientDocuments?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (clientDocuments?.totalCount !== undefined) {
      rowCountRef.current = clientDocuments?.totalCount;
    }
    return rowCountRef.current;
  }, [clientDocuments?.totalCount]);

  const column: GridColDef[] = [
    {
      field: "documentName",
      headerName: "Document Name",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="documentName"
          sortField="documentname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <Typography sx={{ display: "contents" }} color="primary">
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: "dateAdded",
      headerName: "Date Added",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderDatePicker
          field="uploadedAt"
          sortField="dateadded"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY") : " ",
    },
  ];

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
  }, []);

  useEffect(() => {
    if (clientDocuments) {
      const documents: any[] = [];
      clientDocuments.items.forEach((item: any) => {
        documents.push({ ...item, id: item.documentKey });
      });
      setRowData(documents);
    }
  }, [clientDocuments]);

  useEffect(() => {
    if (paginationModel) {
      const pagination = { ...paginationModel };
      pagination.page = paginationModel?.page + 1;
      setFilterParams({ ...filterParams, ...pagination });
    }
  }, [paginationModel]); // eslint-disable-line

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      filters.id = data?.id;
      dispatch(getClientDocumentsRequest(filters));
    }
  }, [filterParams]); // eslint-disable-line

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onFilterUsers = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  const onClearAllFilters = () => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setFilterParams({
      page: 1,
      pageSize: 10,
    } as any);
    setFilterResetKey((prev) => prev + 1);
  };

  const handleAddDocumentClick = () => {
    setShowAddDocument(true);
  };

  const onDeleteClick = async () => {
    const documentKey: string = String(rowSelectionModel[0]);
    setShowLoader(true);
    const response = await deleteClientDocumentRequest(data?.id, documentKey);
    setShowLoader(false);
    if (response?.status === 204) {
      setFilterParams({ ...filterParams });
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const onDownloadClick = async () => {
    const documentKey: string = String(rowSelectionModel[0]);
    const selected = rowData.find((r) => r.id === documentKey);
    const filename = selected?.documentName || "document";
    setShowLoader(true);
    try {
      const response = await downloadTemporaryFileRequest(documentKey);
      if (response && response.status === 200) {
        let fileUrl = response.data;

        if (typeof fileUrl === "string") {
          fileUrl = fileUrl.trim();
          if (fileUrl.startsWith('"') && fileUrl.endsWith('"')) {
            fileUrl = JSON.parse(fileUrl);
          }
        }

        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = filename;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (response && response.status >= 400) {
        setShowErrorNotification(true);
        setErrorMessage("Failed to download document. Please try again.");
      }
    } catch (error) {
      setShowErrorNotification(true);
      setErrorMessage("Failed to download document. Please try again.");
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <>
      <Typography sx={{ fontSize: 24, fontWeight: 400, mt: 2 }}>
        Documents
      </Typography>
      <Box sx={{ my: 1, display: "flex", justifyContent: "flex-end" }}>
        <Button
          className="primary-bg"
          id="add-button"
          variant="contained"
          onClick={handleAddDocumentClick}
          startIcon={<AddIcon />}
        >
          Add Documents
        </Button>
      </Box>
      <Box
        sx={{
          my: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display={"flex"} alignItems={"center"}>
          <IconButton
            aria-label="download"
            disabled={rowSelectionModel.length !== 1}
            onClick={onDownloadClick}
          >
            <DownloadOutlinedIcon
              color={rowSelectionModel.length !== 1 ? "disabled" : "primary"}
            />
          </IconButton>
          <IconButton
            aria-label="delete"
            disabled={rowSelectionModel.length !== 1}
            onClick={onDeleteClick}
          >
            <DeleteOutlinedIcon
              color={rowSelectionModel.length !== 1 ? "disabled" : "error"}
            />
          </IconButton>
        </Box>
        <Box display={"flex"} sx={{ float: "left" }}>
          <IconButton aria-label="clear-all" onClick={onClearAllFilters}>
            <ClearAllOutlined color="primary" />
          </IconButton>
          <IconButton
            aria-label="filter"
            onClick={() => onFilterClick(!showTableFilter)}
          >
            {showTableFilter ? (
              <FilterAltOffOutlinedIcon color="primary" />
            ) : (
              <FilterAltOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ ml: 1, fontSize: 14 }}>
          Documents will be sent for your electronic signature via
          {`{e-sign platform}`}. After being signed, they will be available
          here.
        </Typography>
      </Box>
      <Box sx={{ mx: 1 }}>
        <Table
          key={filterResetKey}
          rowData={rowData}
          columnDef={column}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          disableColumnMenu
          headerHeight={headerHeight}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
        />
      </Box>
      {showAddDocument && (
        <AddDocument
          closeDialog={() => setShowAddDocument(false)}
          showDialog={showAddDocument}
          data={data}
        />
      )}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
      {showLoader && <Loading />}
    </>
  );
}
